import { useSubThemeProvider } from "@judo/core";
import type { VisualElement } from "@judo/model-api";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { ReactNode } from "react";

export interface SubThemeWrapperProps {
	/** The visual element that may have a subTheme property */
	element: VisualElement;
	/** Optional transfer data passed to the sub-theme hook */
	data?: Record<string, unknown>;
	/** Child elements to render within the scoped theme */
	children: ReactNode;
}

/**
 * Wraps children in a scoped MUI ThemeProvider when the element has a `subTheme` set
 * and a matching provider is registered in `CustomizationsConfig.subThemeProviders`.
 *
 * Sub-themes nest naturally: a child element's sub-theme is deep-merged on top of
 * the parent's sub-theme, giving inner themes higher precedence.
 *
 * If `element.subTheme` is not set or no matching provider is found, renders
 * children directly without any wrapper.
 */
export function SubThemeWrapper({ element, data, children }: SubThemeWrapperProps) {
	const subThemeHook = useSubThemeProvider(element);

	if (!subThemeHook) {
		return <>{children}</>;
	}

	return (
		<ThemeProvider theme={(parentTheme) => createTheme(parentTheme, subThemeHook(parentTheme, data))}>
			{children}
		</ThemeProvider>
	);
}
